// Barra lateral del portal (rediseño, docs/PLAN-REDISENO-PORTAL.md §6.2):
// marca, las secciones con icono y texto, y "Plegar menú" abajo. Plegada
// queda en 56px con solo iconos y el nombre en `title`; la preferencia la
// recuerda el navegador (navPreference.ts). Sin preferencia es ancha en
// escritorio y compacta en tablet (hasta xl): lo decide el CSS, no un
// listener de resize.
//
// En el celular (hasta md) no es una barra: es un cajon que abre el boton de
// menu de la cabecera, con velo y Escape, y siempre con los nombres a la
// vista. El cajon se cierra al elegir una seccion.
//
// Un item puede traer un contador (Viajes: lo que espera accion del
// proveedor), que pinta `SidebarCounter`. Ancha, va a la derecha del nombre;
// plegada, en la esquina del icono, para que se vea sin abrir la barra. El
// numero es decorativo (aria-hidden): lo que se lee es "Viajes, 3 viajes
// pendientes".
//
// Es un <nav> con links reales (hrefFor): abrir en otra pestana y el boton
// atras funcionan como en cualquier sitio. El shell decide a donde va cada
// link; la barra no conoce la ruta mas que por `active`.
import { Brand } from "../components/ui/brand.js";
import { cn } from "../components/ui/cn.js";
import { Icon, type IconName } from "../components/ui/icon.js";
import { badgeText } from "../logic/header.js";
import type { NavPreference } from "../logic/navigation.js";
import type { ReactNode } from "react";

export interface SidebarItem {
  readonly path: string;
  readonly label: string;
  readonly href: string;
  readonly icon?: IconName;
  /** El contador (types.ts, NavBadges); lo ubica la barra. */
  readonly badge?: ReactNode;
}

interface Props {
  items: readonly SidebarItem[];
  activePath: string | undefined;
  preference: NavPreference | null;
  /** El cajon del celular esta abierto. */
  drawerOpen: boolean;
  onNavigate: (path: string, event: React.MouseEvent) => void;
  onToggle: () => void;
  onCloseDrawer: () => void;
}

/** Clases del nombre de cada seccion: visible, o solo para lectores de pantalla, segun preferencia y ancho. */
function labelClass(preference: NavPreference | null): string {
  if (preference === "ancha") return "";
  if (preference === "compacta") return "md:sr-only";
  return "md:sr-only xl:not-sr-only";
}

/** El chevron de "Plegar menú" apunta a donde va la barra: adentro si esta ancha, afuera si esta compacta. */
function chevronClass(preference: NavPreference | null): string {
  if (preference === "ancha") return "";
  if (preference === "compacta") return "md:rotate-180";
  return "md:rotate-180 xl:rotate-0";
}

/** El contador: junto al nombre si la barra esta ancha, en la esquina del icono si esta plegada. */
function badgeClass(preference: NavPreference | null): string {
  if (preference === "ancha") return "ml-auto";
  if (preference === "compacta") return "ml-auto md:absolute md:right-1 md:top-0.5";
  return "ml-auto md:absolute md:right-1 md:top-0.5 xl:static xl:ml-auto";
}

function widthClass(preference: NavPreference | null): string {
  if (preference === "ancha") return "md:w-nav";
  if (preference === "compacta") return "md:w-nav-compacta";
  return "md:w-nav-compacta xl:w-nav";
}

export function Sidebar({ items, activePath, preference, drawerOpen, onNavigate, onToggle, onCloseDrawer }: Props): React.JSX.Element {
  const label = labelClass(preference);
  const badgePosition = badgeClass(preference);
  const compactCentered = preference === "compacta" ? "md:justify-center md:px-0" : preference === null ? "md:justify-center md:px-0 xl:justify-start xl:px-2.5" : "";
  return (
    <>
      {/* Velo del cajon: solo en el celular, solo abierto. */}
      <div
        aria-hidden="true"
        onClick={onCloseDrawer}
        className={cn("fixed inset-0 z-40 bg-pagina/70 md:hidden", drawerOpen ? "block" : "hidden")}
      />
      <aside
        data-cajon={drawerOpen ? "abierto" : "cerrado"}
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-nav flex-col overflow-hidden border-r border-divisor bg-pagina",
          "transition-[transform,width] duration-200 ease-estandar",
          "md:sticky md:top-0 md:h-screen md:translate-x-0 md:bg-pagina/85",
          drawerOpen ? "translate-x-0 shadow-panel" : "-translate-x-full",
          widthClass(preference),
        )}
      >
        <div className="flex h-cab shrink-0 items-center gap-2.5 border-b border-divisor px-3.5">
          <Brand size="sm" wordClassName={label} />
          <span className={cn("rotulo ml-auto text-micro text-texto-debil", label)}>Proveedor</span>
          <button
            type="button"
            onClick={onCloseDrawer}
            aria-label="Cerrar el menú"
            className="ml-auto grid size-9 place-items-center rounded-md text-texto-suave hover:bg-velo md:hidden"
          >
            <Icon name="cerrar" className="size-5" />
          </button>
        </div>

        <nav aria-label="Secciones" className="flex min-h-0 flex-1 flex-col">
          <ul className="m-0 flex list-none flex-col gap-0.5 p-2">
            {items.map((item) => {
              const current = item.path === activePath;
              return (
                <li key={item.path}>
                  <a
                    href={item.href}
                    aria-current={current ? "page" : undefined}
                    title={item.label}
                    onClick={(event) => onNavigate(item.path, event)}
                    className={cn(
                      "relative flex h-9 items-center gap-2.5 rounded-md px-2.5 text-sm font-medium no-underline transition-colors duration-150 ease-estandar",
                      "hover:bg-velo hover:text-texto-2 hover:no-underline",
                      current ? "bg-velo-activo text-blanco" : "text-texto-suave",
                      compactCentered,
                    )}
                  >
                    {item.icon !== undefined && <Icon name={item.icon} className="size-[18px]" />}
                    <span className={cn("whitespace-nowrap", label)}>{item.label}</span>
                    {item.badge !== undefined && <span className={cn("inline-flex", badgePosition)}>{item.badge}</span>}
                  </a>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="mt-auto hidden border-t border-divisor p-2 md:block">
          <button
            type="button"
            onClick={onToggle}
            aria-label="Plegar o desplegar el menú"
            className={cn(
              "flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-texto-suave hover:bg-velo hover:text-texto-2",
              compactCentered,
            )}
          >
            <Icon name="plegar" className={cn("size-[18px] transition-transform duration-200 ease-estandar", chevronClass(preference))} />
            <span className={cn("whitespace-nowrap", label)}>Plegar menú</span>
          </button>
        </div>
      </aside>
    </>
  );
}

/**
 * El contador naranja de un item: el numero (hasta 99, despues "99+") a la
 * vista y el texto completo para lectores de pantalla. Sin pendientes no se
 * pinta: un cero no pide nada. Texto naranja claro sobre slate 800, no blanco
 * sobre naranja (que no llega a 4.5:1).
 */
export function SidebarCounter({ count, label }: { count: number; label: string }): React.JSX.Element | null {
  if (count <= 0) return null;
  return (
    <>
      <span className="sr-only">{`, ${label}`}</span>
      <span
        aria-hidden="true"
        data-contador=""
        className="tabular grid h-[18px] min-w-[18px] place-items-center rounded-pill border border-naranja/60 bg-superficie px-1 text-micro font-semibold leading-none text-naranja-texto"
      >
        {badgeText(count)}
      </span>
    </>
  );
}
